import * as ToolbarPrimitive from "@radix-ui/react-toolbar"
import { forwardRef, type MouseEvent, type ReactNode } from "react"

import type { SX } from "../../theme"
import { Button } from "../Button"
import { ButtonGroup, useButtonGroup } from "../ButtonGroup"
import type { IconName } from "../Icon"
import { Select, SelectItem } from "../Select"
import { Separator } from "../Separator"
import { ToggleButton } from "../ToggleButton"
import styles from "./Toolbar.module.css"

export interface ToolbarProps {
  children?: ReactNode
  sx?: SX
}

export function Toolbar(props: ToolbarProps) {
  const { sx, ...otherProps } = props

  return (
    <ToolbarPrimitive.Root
      {...otherProps}
      className={styles.toolbar}
      style={sx}
    />
  )
}

interface ToolbarButtonProps {
  children?: ReactNode
  disabled?: boolean
  startIcon?: IconName
  endIcon?: IconName
  onClick?: (event: MouseEvent) => void
}

export const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  function ToolbarButton(props, ref) {
    const buttonGroup = useButtonGroup()
    const { children, disabled = buttonGroup?.disabled, ...otherProps } = props

    return (
      <ToolbarPrimitive.Button
        {...otherProps}
        asChild
        disabled={disabled}
        ref={ref}
      >
        <Button size="small" color="grey">
          {children}
        </Button>
      </ToolbarPrimitive.Button>
    )
  }
)

interface ToolbarButtonGroupProps {
  children?: ReactNode
  density?: "regular" | "compact"
  disabled?: boolean
}

export function ToolbarButtonGroup(props: ToolbarButtonGroupProps) {
  return <ButtonGroup {...props} />
}

interface ToolbarSelectProps {
  children?: ReactNode
  placeholder?: string
  disabled?: boolean
  value?: string
  onValueChange?: (value: string) => void
}

export function ToolbarSelect(props: ToolbarSelectProps) {
  return (
    <Select
      {...props}
      size="small"
      color="grey"
      renderTrigger={({ button, disabled }) => (
        <ToolbarPrimitive.Button asChild disabled={disabled}>
          {button}
        </ToolbarPrimitive.Button>
      )}
    />
  )
}

interface ToolbarSelectItemProps {
  children?: ReactNode
  disabled?: boolean
  textValue?: string
  value: string
}

export function ToolbarSelectItem(props: ToolbarSelectItemProps) {
  return <SelectItem {...props} />
}

export function ToolbarSeparator() {
  return (
    <ToolbarPrimitive.Separator asChild>
      <Separator orientation="vertical" />
    </ToolbarPrimitive.Separator>
  )
}

interface ToolbarToggleButtonProps {
  children?: ReactNode
  disabled?: boolean
  startIcon?: IconName
  endIcon?: IconName
  pressed?: boolean
  onPressedChange?: (pressed: boolean) => void
}

export function ToolbarToggleButton(props: ToolbarToggleButtonProps) {
  const buttonGroup = useButtonGroup()
  const { children, disabled = buttonGroup?.disabled, ...otherProps } = props

  return (
    <ToolbarPrimitive.Button {...otherProps} asChild disabled={disabled}>
      <ToggleButton size="small" color="grey">
        {children}
      </ToggleButton>
    </ToolbarPrimitive.Button>
  )
}

type SingleGroupProps = Pick<
  ToolbarPrimitive.ToolbarToggleGroupSingleProps,
  "type" | "value" | "onValueChange"
>

type MultipleGroupProps = Pick<
  ToolbarPrimitive.ToolbarToggleGroupMultipleProps,
  "type" | "value" | "onValueChange"
>

type GroupProps = SingleGroupProps | MultipleGroupProps

type ToolbarToggleButtonGroupProps = {
  children?: ReactNode
  disabled?: boolean
  density?: "regular" | "compact"
} & GroupProps

export function ToolbarToggleButtonGroup(props: ToolbarToggleButtonGroupProps) {
  const { children, density, ...otherProps } = props

  return (
    <ToolbarPrimitive.ToggleGroup {...otherProps} asChild>
      <ButtonGroup density={density}>{children}</ButtonGroup>
    </ToolbarPrimitive.ToggleGroup>
  )
}

interface ToolbarToggleButtonGroupItemProps {
  children?: ReactNode
  disabled?: boolean
  value: string
  endIcon?: IconName
  startIcon?: IconName
}

export function ToolbarToggleButtonGroupItem(
  props: ToolbarToggleButtonGroupItemProps
) {
  const { children, ...otherProps } = props

  return (
    <ToolbarPrimitive.ToggleItem {...otherProps} asChild>
      <Button size="small" color="grey">
        {children}
      </Button>
    </ToolbarPrimitive.ToggleItem>
  )
}
